/* GrowthOS — Create Ticket modal for selected campaigns.
   Mirror of the leads/contacts/accounts/deals create-ticket modal UI; wired to the
   real ticket API (apiServices.createTicketList) using the same payload the legacy
   campaigns ChildTicket flow used (priorityId / ownerId / statusId + campaignId of
   the selected campaigns, comma-joined — matches campaigns.jsx's existing
   handleFormSubmit). */
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import apiServices from '../../services/service';
import { ticketInputData } from '../../shared/constants';
import { fetchDataAndUpdateStore } from '../../redux/actions';

const PATHS = {
  ticket: 'M4 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4V7ZM14 5v14',
  x: 'M18 6 6 18M6 6l12 12',
  chevD: 'M6 9l6 6 6-6',
  check: 'M20 6 9 17l-5-5',
  search: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM21 21l-4.3-4.3',
  info: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 16v-4M12 8h.01',
};
const Ic = ({ name, size = 16, className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d={PATHS[name]} />
  </svg>
);

const priColor = (label = '') => {
  const s = label.toLowerCase();
  if (s.includes('urgent') || s.includes('critical')) return 'var(--gos-red-fg)';
  if (s.includes('high')) return 'var(--gos-rose-fg)';
  if (s.includes('med')) return 'var(--gos-amber-fg)';
  if (s.includes('low')) return 'var(--gos-blue-fg)';
  return 'var(--gos-gray-fg)';
};
const statusColor = (label = '') => {
  const s = label.toLowerCase();
  if (s.includes('progress')) return 'var(--gos-amber-fg)';
  if (s.includes('wait') || s.includes('hold') || s.includes('pending')) return 'var(--gos-gray-fg)';
  if (s.includes('clos') || s.includes('resolv') || s.includes('complet') || s.includes('done')) return 'var(--gos-green-fg)';
  if (s.includes('open') || s.includes('new')) return 'var(--gos-blue-fg)';
  return 'var(--gos-gray-fg)';
};

// reusable dropdown (button + popup, optional search, color dots / sub labels)
const Dropdown = ({ value, placeholder, items, onChange, searchable }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setQ('');
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const sel = items.find((i) => i.value === value);
  const list = searchable && q ? items.filter((i) => i.label.toLowerCase().includes(q.toLowerCase())) : items;
  return (
    <div className="gos-dd" ref={ref}>
      <button
        type="button"
        className={'gos-dd-btn' + (open ? ' open' : '') + (sel ? ' has-val' : '')}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        {sel && sel.color ? <span className="gos-dd-dot" style={{ background: sel.color }} /> : null}
        <span className="gos-dd-label">{sel ? sel.label : placeholder || 'Select…'}</span>
        <Ic name="chevD" size={14} style={{ flex: 'none', color: 'var(--gos-text-3)' }} />
      </button>
      {open && (
        <div className="gos-dd-pop">
          {searchable ? (
            <div className="gos-dd-search">
              <Ic name="search" size={14} />
              <input autoFocus placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
          ) : null}
          <div className="gos-dd-list">
            {list.length === 0 ? <div className="gos-dd-empty">No matches</div> : null}
            {list.map((i) => (
              <button
                key={i.value}
                type="button"
                className={'gos-dd-item' + (i.value === value ? ' on' : '')}
                onClick={() => {
                  onChange(i.value);
                  setOpen(false);
                  setQ('');
                }}
              >
                {i.color ? <span className="gos-dd-dot" style={{ background: i.color }} /> : null}
                <span className="gos-dd-grow">
                  {i.label}
                  {i.sub ? <span className="gos-dd-sub">{i.sub}</span> : null}
                </span>
                {i.value === value ? <Ic name="check" size={14} style={{ color: 'var(--gos-accent)' }} /> : null}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, required, full, children }) => (
  <div className={'gos-tkt-field' + (full ? ' full' : '')}>
    <label className="gos-tkt-label">
      {label}
      {required ? <span className="gos-tkt-req">*</span> : null}
    </label>
    {children}
  </div>
);

const REMIND_PRESETS = [
  ['at', 'At due date'],
  ['1h', '1 hour before'],
  ['1d', '1 day before'],
  ['1w', '1 week before'],
  ['custom', 'Custom'],
];

const CampaignsCreateTicketModal = ({ onClose, campaignIds = [], priorities = [], statuses = [], owners = [], defaultOwnerId, onCreated }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const priorityItems = priorities.map((p) => ({ value: p.id, label: p.displayName, color: priColor(p.displayName) }));
  const statusItems = statuses.map((s) => ({ value: s.id, label: s.displayName, color: statusColor(s.displayName) }));
  const ownerItems = owners.map((o) => ({
    value: o.userId,
    label: o.display_name || o.userName || o.name || '',
    sub: o.role || '',
  }));

  const defaultStatus = (statuses.find((s) => String(s.displayName).toLowerCase().includes('open')) || statuses[0] || {}).id;
  const defaultOwner = ownerItems.some((o) => o.value === defaultOwnerId) ? defaultOwnerId : '';

  const [f, setF] = useState({
    subject: '',
    dueDate: '',
    priority: '',
    owner: defaultOwner,
    status: defaultStatus || '',
    description: '',
    reminder: false,
    remindPreset: '1d',
    reminderDate: '',
    reminderTime: '09:00',
  });
  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));
  const valid = f.subject.trim() && f.owner;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !loading) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, loading]);

  const computeReminder = () => {
    if (!f.reminder) return null;
    if (f.remindPreset === 'custom') {
      return f.reminderDate ? `${f.reminderDate}T${f.reminderTime || '09:00'}` : null;
    }
    if (!f.dueDate) return null;
    const base = new Date(`${f.dueDate}T09:00`);
    if (Number.isNaN(base.getTime())) return null;
    if (f.remindPreset === '1h') base.setHours(base.getHours() - 1);
    else if (f.remindPreset === '1d') base.setDate(base.getDate() - 1);
    else if (f.remindPreset === '1w') base.setDate(base.getDate() - 7);
    return base.toISOString();
  };

  const submit = () => {
    if (!valid || loading) return;
    setLoading(true);
    const payload = {
      subject: f.subject.trim(),
      dueDate: f.dueDate || null,
      priorityId: f.priority || null,
      ownerId: f.owner,
      statusId: f.status || null,
      description: f.description || '',
      reminder: computeReminder(),
      campaignId: (campaignIds || []).join(','),
    };
    apiServices
      .createTicketList(payload)
      .then((response) => {
        if (response.status === 200) {
          toast.success('Ticket created successfully!', { position: 'top-right' });
          dispatch(fetchDataAndUpdateStore({ key: 'ticketList', inputData: ticketInputData }));
          setLoading(false);
          if (onCreated) onCreated();
          onClose();
        } else {
          setLoading(false);
          const msgs = [];
          for (const key in response.errors || {}) {
            if (Object.prototype.hasOwnProperty.call(response.errors, key)) msgs.push(...response.errors[key]);
          }
          if (msgs.length) msgs.forEach((m) => toast.error(m, { position: 'top-right' }));
          else toast.error('Could not create ticket.', { position: 'top-right' });
        }
      })
      .catch((error) => {
        const errs = error.response?.data?.errors;
        if (errs) {
          Object.keys(errs).forEach((k) => errs[k].forEach((m) => toast.error(m, { position: 'top-right' })));
        } else {
          toast.error('An unexpected error occurred.', { position: 'top-right' });
        }
        setLoading(false);
      });
  };

  const scopeCount = campaignIds.length;

  return (
    <div
      className="gos-import-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !loading) onClose();
      }}
    >
      <div className="gos-tkt-modal" role="dialog" aria-modal="true" aria-label="Create ticket">
        <div className="gos-import-head">
          <div className="gos-import-head-l">
            <span className="gos-import-step-ic"><Ic name="ticket" size={17} /></span>
            <div>
              <div className="gos-import-title">Create Ticket</div>
              <div className="gos-import-sub">Log a new ticket for the selected campaign{scopeCount > 1 ? 's' : ''}</div>
            </div>
          </div>
          <button type="button" className="gos-import-close" onClick={onClose} aria-label="Close" disabled={loading}>
            <Ic name="x" size={18} />
          </button>
        </div>

        <div className="gos-tkt-body">
          {scopeCount > 0 && (
            <div className="gos-tkt-scope">
              <Ic name="check" size={13} />
              Applies to <strong>{scopeCount} selected campaign{scopeCount > 1 ? 's' : ''}</strong>
            </div>
          )}
          <div className="gos-tkt-grid">
            <Field label="Subject" required>
              <input className="gos-tkt-input" placeholder="Enter subject" value={f.subject} onChange={(e) => set('subject', e.target.value)} autoFocus />
            </Field>
            <Field label="Due Date">
              <input type="date" className="gos-tkt-input" value={f.dueDate} onChange={(e) => set('dueDate', e.target.value)} />
            </Field>
            <Field label="Priority">
              <Dropdown value={f.priority} placeholder="Select priority…" items={priorityItems} onChange={(v) => set('priority', v)} />
            </Field>
            <Field label="Ticket Owner" required>
              <Dropdown value={f.owner} placeholder="Select owner…" items={ownerItems} searchable onChange={(v) => set('owner', v)} />
            </Field>
            <Field label="Status">
              <Dropdown value={f.status} placeholder="Select status…" items={statusItems} onChange={(v) => set('status', v)} />
            </Field>
            <Field label="Description">
              <textarea className="gos-tkt-input gos-tkt-textarea" placeholder="Enter description" rows={3} value={f.description} onChange={(e) => set('description', e.target.value)} />
            </Field>
            <Field label="Reminder" full>
              <div className="gos-tkt-remind-head">
                <button
                  type="button"
                  role="switch"
                  aria-checked={f.reminder}
                  className={'gos-tkt-toggle' + (f.reminder ? ' on' : '')}
                  onClick={() => set('reminder', !f.reminder)}
                >
                  <span className="gos-tkt-toggle-knob" />
                </button>
                <span className="gos-tkt-toggle-hint">{f.reminder ? 'Notify the ticket owner' : 'Off'}</span>
              </div>
              {f.reminder && (
                <div className="gos-tkt-remind-body">
                  <div className="gos-tkt-preset-row">
                    {REMIND_PRESETS.map(([v, lbl]) => (
                      <button key={v} type="button" className={'gos-tkt-preset' + (f.remindPreset === v ? ' on' : '')} onClick={() => set('remindPreset', v)}>
                        {lbl}
                      </button>
                    ))}
                  </div>
                  {f.remindPreset === 'custom' ? (
                    <div className="gos-tkt-remind-fields">
                      <input type="date" className="gos-tkt-input" value={f.reminderDate} onChange={(e) => set('reminderDate', e.target.value)} />
                      <input type="time" className="gos-tkt-input" value={f.reminderTime} onChange={(e) => set('reminderTime', e.target.value)} />
                    </div>
                  ) : !f.dueDate ? (
                    <div className="gos-tkt-remind-note">
                      <Ic name="info" size={13} /> Set a due date so we know when to remind.
                    </div>
                  ) : null}
                </div>
              )}
            </Field>
          </div>
        </div>

        <div className="gos-import-foot end">
          <button type="button" className="gos-btn gos-btn-ghost" onClick={onClose} disabled={loading}>Cancel</button>
          <button type="button" className="gos-btn gos-btn-primary" onClick={submit} disabled={!valid || loading}>
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Saving…
              </>
            ) : (
              <>
                <Ic name="check" size={16} /> Save
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CampaignsCreateTicketModal;
